import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { FacebookIcon, InstagramIcon, YoutubeIcon, MailIcon, PhoneIcon, MapPinIcon, ArrowRightIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { XLogoIcon } from './shared/Icons';
import { privacyPolicyEN, privacyPolicyES, accessibilityEN, accessibilityES, accessibilityContactEN, accessibilityContactES, accessibilityContactSuffixEN, accessibilityContactSuffixES, LegalDocument } from '../data/legal-content';
export function Footer() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  const {
    t,
    language
  } = useLanguage();
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [accessibilityOpen, setAccessibilityOpen] = useState(false);
  const isEs = language === 'ES';
  const quickLinks = [{
    labelKey: 'footer.link.home',
    href: '#home'
  }, {
    labelKey: 'footer.link.mission',
    href: '#mission'
  }, {
    labelKey: 'footer.link.ourStories',
    href: '#our-stories'
  }, {
    labelKey: 'footer.link.community',
    href: '#community'
  }, {
    labelKey: 'footer.link.hondurasFacts',
    href: '#community'
  }, {
    labelKey: 'footer.link.news',
    href: '#news'
  }, {
    labelKey: 'footer.link.leadership',
    href: '#leadership'
  }, {
    labelKey: 'footer.link.consular',
    href: '#consular-affairs'
  }, {
    labelKey: 'footer.link.exploreHonduras',
    href: '#explore-honduras'
  }, {
    labelKey: 'footer.link.businessInvestments',
    href: '#business-investments'
  }, {
    labelKey: 'footer.link.upliftingVoices',
    href: '#social-voices'
  }, {
    labelKey: 'footer.link.nonprofitDirectory',
    href: '#nonprofit-directory'
  }, {
    labelKey: 'footer.link.faq',
    href: '#faq'
  }];
  const privacyPolicy: LegalDocument = isEs ? privacyPolicyES : privacyPolicyEN;
  const accessibilityParagraphs = isEs ? accessibilityES : accessibilityEN;
  const accessibilityContact = isEs ? accessibilityContactES : accessibilityContactEN;
  const accessibilityContactSuffix = isEs ? accessibilityContactSuffixES : accessibilityContactSuffixEN;
  return <footer id="contact" className="bg-haa-navy" aria-label="Site footer" data-id="element-2358">
      {/* Smooth gradient into footer */}
      <div className="h-16 bg-gradient-to-b from-gray-50 to-haa-navy" data-id="element-2359" />

      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8" data-id="element-2360">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 mb-12" data-id="element-2361">
          {/* Column 1: About + Contact */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6
        }} data-id="element-2362">
            <div className="flex items-center gap-3 mb-5" data-id="element-2363">
              <img src="https://cdn.magicpatterns.com/uploads/b1GB8Z9aEvoE58p9BdX4qx/HonduranAmericanLogoNew.png" alt="Honduran American Association" className="h-14 w-auto object-contain brightness-0 invert" data-id="element-2364" />
            </div>

            <div className="flex items-center gap-3 mb-6" data-id="element-2365">
              {[{
              Icon: FacebookIcon,
              gradient: 'from-haa-blue to-blue-900',
              label: 'Facebook',
              href: '#'
            }, {
              Icon: InstagramIcon,
              gradient: 'from-haa-midblue to-haa-skyblue',
              label: 'Instagram',
              href: 'https://www.instagram.com/honduranamerican/'
            }, {
              Icon: XLogoIcon,
              gradient: 'from-haa-skyblue to-blue-400',
              label: 'X',
              href: 'https://x.com/jaymiranda'
            }, {
              Icon: YoutubeIcon,
              gradient: 'from-haa-blue to-haa-navy',
              label: 'YouTube',
              href: '#'
            }].map(({
              Icon,
              gradient,
              label,
              href
            }) => <motion.a key={label} href={href} target={href !== '#' ? '_blank' : undefined} rel={href !== '#' ? 'noopener noreferrer' : undefined} aria-label={label} whileHover={{
              scale: 1.2,
              boxShadow: '0 0 15px rgba(74, 144, 217, 0.4)'
            }} whileTap={{
              scale: 0.9
            }} className={`w-9 h-9 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center shadow-lg`} data-id="element-2366">
                  <Icon className="w-4 h-4 text-white" data-id="element-2367" />
                </motion.a>)}
            </div>

            {/* Contact Us */}
            <h3 className="font-bebas text-haa-skyblue text-2xl tracking-widest mb-4" data-id="element-2368">
              {t('footer.contact')}
            </h3>
            <ul className="space-y-3" data-id="element-2369">
              <li className="flex items-start gap-3" data-id="element-2370">
                <MapPinIcon className="w-4 h-4 text-haa-skyblue flex-shrink-0 mt-0.5" data-id="element-2371" />
                <span className="font-nunito text-white/60 text-sm" data-id="element-2372">
                  Washington D.C.
                </span>
              </li>
              <li className="flex items-center gap-3" data-id="element-2373">
                <PhoneIcon className="w-4 h-4 text-haa-skyblue flex-shrink-0" data-id="element-2374" />
                <a href="tel:+17033131465" className="font-nunito text-white/60 hover:text-white text-sm transition-colors" data-id="element-2375">
                  +1-703-313-1465
                </a>
              </li>
              <li className="flex items-center gap-3" data-id="element-2376">
                <svg className="w-4 h-4 text-haa-skyblue flex-shrink-0" viewBox="0 0 24 24" fill="currentColor" data-id="element-2377">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" data-id="element-2378" />
                </svg>
                <a href="https://wa.me/17033131465" target="_blank" rel="noopener noreferrer" className="font-nunito text-white/60 hover:text-white text-sm transition-colors" data-id="element-2379">
                  {isEs ? 'Enviar mensaje por WhatsApp' : 'Message us on WhatsApp'}
                </a>
              </li>
              <li className="flex items-center gap-3" data-id="element-2380">
                <MailIcon className="w-4 h-4 text-haa-skyblue flex-shrink-0" data-id="element-2381" />
                <a href="mailto:info@honduranamerican.org" className="font-nunito text-white/60 hover:text-white text-sm transition-colors" data-id="element-2382">
                  info@honduranamerican.org
                </a>
              </li>
            </ul>
          </motion.div>

          {/* Column 2: Quick Links */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.1
        }} data-id="element-2383">
            <h3 className="font-bebas text-haa-skyblue text-2xl tracking-widest mb-5" data-id="element-2384">
              {t('footer.quickLinks')}
            </h3>
            <ul className="space-y-2.5" data-id="element-2385">
              {quickLinks.map(link => <li key={link.labelKey} data-id="element-2386">
                  <a href={link.href} className="font-nunito text-white/60 hover:text-haa-skyblue text-sm flex items-center gap-2 group transition-colors" data-id="element-2387">
                    <ArrowRightIcon className="w-3.5 h-3.5 text-haa-blue group-hover:text-haa-skyblue transition-colors flex-shrink-0" data-id="element-2388" />
                    {t(link.labelKey)}
                  </a>
                </li>)}
            </ul>
          </motion.div>
        </div>

        {/* Divider */}
        <div className="h-px w-full bg-gradient-to-r from-transparent via-haa-skyblue/20 to-transparent mb-8" data-id="element-2389" />

        {/* Bottom Bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-2390">
          <p className="font-nunito text-white/40 text-sm text-center sm:text-left" data-id="element-2391">
            {t('footer.copyright')}
          </p>
          <div className="flex items-center gap-4" data-id="element-2392">
            <button onClick={() => setPrivacyOpen(true)} className="font-nunito text-white/40 hover:text-white/70 text-sm transition-colors cursor-pointer bg-transparent border-none" data-id="element-2393">
              {t('footer.privacy')}
            </button>
            <span className="text-white/20" data-id="element-2394">·</span>
            <button onClick={() => setAccessibilityOpen(true)} className="font-nunito text-white/40 hover:text-white/70 text-sm transition-colors cursor-pointer bg-transparent border-none" data-id="element-2395">
              {t('footer.accessibility')}
            </button>
          </div>
        </div>
      </div>

      {/* Privacy Policy Modal */}
      <AnimatePresence data-id="element-2396">
        {privacyOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setPrivacyOpen(false)} data-id="element-2397">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3,
          ease: [0.16, 1, 0.3, 1]
        }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-2398">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100" data-id="element-2399">
                <div className="flex items-center gap-3" data-id="element-2400">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-haa-blue to-haa-navy flex items-center justify-center shadow-sm" data-id="element-2401">
                    <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-2402">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" data-id="element-2403" />
                    </svg>
                  </div>
                  <h2 className="font-bebas text-3xl text-haa-navy tracking-wider" data-id="element-2404">
                    {isEs ? 'Política de Privacidad' : 'Privacy Policy'}
                  </h2>
                </div>
                <button onClick={() => setPrivacyOpen(false)} className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600" aria-label="Close" data-id="element-2405">
                  <XIcon className="w-5 h-5" data-id="element-2406" />
                </button>
              </div>

              {/* Content — rendered from data */}
              <div className="overflow-y-auto px-6 py-6 space-y-4" data-id="element-2407">
                {privacyPolicy.effectiveDate && <p className="font-nunito text-gray-500 text-xs" data-id="element-2408">
                    {isEs ? 'Fecha de Vigencia' : 'Effective Date'}:{' '}
                    {privacyPolicy.effectiveDate}
                    <br data-id="element-2409" />
                    {isEs ? 'Última Actualización' : 'Last Updated'}:{' '}
                    {privacyPolicy.lastUpdated}
                  </p>}
                <p className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2410">
                  {privacyPolicy.intro}
                </p>
                {privacyPolicy.sections.map((section, i) => <div key={i} data-id="element-2411">
                    {section.heading && <h3 className="font-bebas text-lg text-haa-navy mt-4" data-id="element-2412">
                        {section.heading}
                      </h3>}
                    {section.isContact ? <p className="font-nunito text-gray-600 text-sm leading-relaxed whitespace-pre-line" data-id="element-2413">
                        {section.body}
                      </p> : section.body.split('\n\n').map((paragraph, pi) => <p key={pi} className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2414">
                          {paragraph.includes(':') && paragraph.indexOf(':') < 60 ? <>
                              <strong data-id="element-2415">
                                {paragraph.slice(0, paragraph.indexOf(':') + 1)}
                              </strong>
                              {paragraph.slice(paragraph.indexOf(':') + 1)}
                            </> : paragraph}
                        </p>)}
                  </div>)}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50" data-id="element-2416">
                <button onClick={() => setPrivacyOpen(false)} className="w-full py-2.5 bg-haa-navy hover:bg-haa-blue text-white font-nunito font-bold text-sm rounded-xl transition-colors" data-id="element-2417">
                  {isEs ? 'Cerrar' : 'Close'}
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Accessibility Statement Modal */}
      <AnimatePresence data-id="element-2418">
        {accessibilityOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setAccessibilityOpen(false)} data-id="element-2419">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3,
          ease: [0.16, 1, 0.3, 1]
        }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-2420">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100" data-id="element-2421">
                <div className="flex items-center gap-3" data-id="element-2422">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-haa-blue to-haa-navy flex items-center justify-center shadow-sm" data-id="element-2423">
                    <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-2424">
                      <circle cx="12" cy="4.5" r="2.5" data-id="element-2425" />
                      <path d="M12 7v5" data-id="element-2426" />
                      <path d="M8 22l4-10 4 10" data-id="element-2427" />
                      <path d="M6 12h12" data-id="element-2428" />
                    </svg>
                  </div>
                  <h2 className="font-bebas text-3xl text-haa-navy tracking-wider" data-id="element-2429">
                    {isEs ? 'Declaración de Accesibilidad' : 'Accessibility Statement'}
                  </h2>
                </div>
                <button onClick={() => setAccessibilityOpen(false)} className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600" aria-label="Close" data-id="element-2430">
                  <XIcon className="w-5 h-5" data-id="element-2431" />
                </button>
              </div>

              {/* Content — rendered from data */}
              <div className="overflow-y-auto px-6 py-6 space-y-4" data-id="element-2432">
                {accessibilityParagraphs.map((paragraph, i) => <p key={i} className="font-nunito text-gray-600 text-sm leading-relaxed" data-id="element-2433">
                    {paragraph}
                  </p>)}
                <div className="mt-2 p-4 bg-blue-50 rounded-xl border border-blue-100" data-id="element-2434">
                  <p className="font-nunito text-gray-700 text-sm leading-relaxed" data-id="element-2435">
                    {accessibilityContact}{' '}
                    <a href="mailto:info@honduranamerican.org" className="text-haa-blue hover:text-haa-navy font-semibold underline transition-colors" data-id="element-2436">
                      info@honduranamerican.org
                    </a>
                    {accessibilityContactSuffix}
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50" data-id="element-2437">
                <button onClick={() => setAccessibilityOpen(false)} className="w-full py-2.5 bg-haa-navy hover:bg-haa-blue text-white font-nunito font-bold text-sm rounded-xl transition-colors" data-id="element-2438">
                  {isEs ? 'Cerrar' : 'Close'}
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </footer>;
}